/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styled, { createGlobalStyle } from 'styled-components';
import _ from 'lodash';
import Config from 'Config';
import TableActionsDropdown from 'common/components/table/TableActionsDropdown';
import PhotoMetadataModal from 'common/components/modal/PhotoMetadataModal';
import DocumentsPhotoModal from 'common/components/modal/DocumentsPhotoModal';
import { DOCUMENT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';

import { displayDateTime } from 'common/utils/date-time';
import { getUsagePhotoByDocIds } from 'admin/services/AdminPhotoService';

import PhotoRequirementsApprovalModal from 'admin/components/work-orders/photoRequirements/PhotoRequirementsApprovalModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import checkedIcon from 'images/svg/checked.svg';
import {
    KebabMenuButton,
    KebabMenuIcon,
} from 'common/components/buttons/ActionIconButtons/KebabMenuButton';

import {
    APPROVAL_STATUS,
    Document,
    Photo,
    PhotoApproval,
} from 'common/types/documentRequirements';
import { getIntegrityDetails } from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploadedRow/WorkOrderRequirementsPhotosUploadedRow';
import EditPhotoRequirementsModal from '../../../../admin/components/work-orders/photoRequirements/EditPhotoRequirementsModal';
import {
    isVideo,
    isPDF,
} from '../DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';
import styles from '../DocumentRequirementsPhotos.module.scss';
import { StyledChip } from '../Shared/StyledChip';

type Props = {
    open: boolean;
    onClose: () => void;

    items: Photo[];
    photosAll: Photo[];
    document: Document;
    photoTypes: any[];
    photoTypeRequirements: any[];
    authenticationData: {
        authenticatedUserCanEdit: boolean;
        authenticatedUserIsCon: boolean;
    };

    handleSaveApprovalPhotoRequirement: (
        photoId: number,
        approval: Partial<PhotoApproval>
    ) => Promise<void>;

    handleEditUploadedPhotoRequirement: (
        photoId: number,
        photoTypeId: string,
        reasonOther: string,
        requiresPullAndClean: boolean
    ) => Promise<void>;

    handleRemovePhotoOnClick: (photo: Photo) => Promise<void>;
    photoRequirementMet: (photo: Photo) => boolean;
    hasApprovalStatus: (photo: Photo) => boolean;
    isRejected: (photo: Photo) => boolean;
    handleGetApprovalHistoryPhotoRequirement: (
        workOrderId: number,
        photoId: number
    ) => Promise<Photo[]>;
    isClientEnablePhotoVerification?: boolean;
};

export const checkShowApprovalRejectButton = (
    photo: Photo,
    document: Document,
    isRejected: (photo: Photo) => boolean,
    isClientEnablePhotoVerification?: boolean
): boolean => {
    return (
        document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER &&
        document.statusId === 'IN_REVIEW' &&
        (isClientEnablePhotoVerification ||
            isPhotoVerificationEnabled(document) ||
            isRejected(photo))
    );
};

const isPhotoVerificationEnabled = (document?: Document): boolean =>
    _.some(
        document?.client?.requirements,
        req =>
            req.requirementTypeId === 'PHOTO' &&
            req.requirement.enableVerification &&
            req.tradeTypeId === document.tradeTypeId &&
            req.tradeSubTypeId === document.tradeSubTypeId &&
            req.documentSubTypeId === document.workOrderTypeId
    );

export default function AdminDocumentRequirementsPhotoReviewMode({
    open,
    onClose,
    items,
    photosAll,
    document,
    authenticationData,
    handleSaveApprovalPhotoRequirement,
    photoTypeRequirements,
    handleEditUploadedPhotoRequirement,
    handleRemovePhotoOnClick,
    photoRequirementMet,
    hasApprovalStatus,
    isRejected,
    photoTypes,
    handleGetApprovalHistoryPhotoRequirement,
    isClientEnablePhotoVerification = false,
}: Props) {
    const [idx, setIdx] = useState(0);
    const [busy, setBusy] = useState(false);

    const [approvalStatus, setApprovalStatus] = useState<string>('');

    const [dupModalData, setDupModalData] = useState<{ refDocs: any[] } | null>(
        null
    );

    const [isMetadataOpen, setIsMetadataOpen] = useState(false);
    const metaAnchorRef = useRef<HTMLDivElement | null>(null);
    const [showEditPhotoDrawer, setShowEditPhotoDrawer] = useState(false);
    const [onEditDoneCallback, setOnEditDoneCallback] = useState<
        (() => void) | null
    >(null);
    const [approvalHistory, setApprovalHistory] = useState<Photo[] | null>(
        null
    );

    const fetchAndSetHistory = useCallback(
        async (photoId: number) => {
            try {
                const history = await handleGetApprovalHistoryPhotoRequirement(
                    document.id,
                    photoId
                );
                setApprovalHistory(history);
            } catch (e) {
                // handle error
            }
        },
        [document.id, handleGetApprovalHistoryPhotoRequirement]
    );

    const showApprovalRejectButton = useCallback(
        (photo: Photo) =>
            checkShowApprovalRejectButton(
                photo,
                document,
                isRejected,
                isClientEnablePhotoVerification
            ),
        [document, isRejected, isClientEnablePhotoVerification]
    );

    const shouldShowApprovalButtons = useCallback(
        (photo: Photo) => {
            return (
                photoRequirementMet(photo) &&
                showApprovalRejectButton(photo) &&
                !hasApprovalStatus(photo) &&
                !isVideo(photo)
            );
        },
        [photoRequirementMet, showApprovalRejectButton, hasApprovalStatus]
    );

    const reviewItems = useMemo(() => {
        return items.filter(shouldShowApprovalButtons);
    }, [items, shouldShowApprovalButtons]);

    const isDone = open && reviewItems.length === 0;
    const current: Photo | undefined = reviewItems[idx];
    const currentId = current?.id;

    const showApprovalButtons = useMemo(() => {
        if (!current) return false;
        return (
            photoRequirementMet(current) &&
            showApprovalRejectButton(current) &&
            !hasApprovalStatus(current)
        );
    }, [
        current,
        photoRequirementMet,
        showApprovalRejectButton,
        hasApprovalStatus,
    ]);

    useEffect(() => {
        if (!open) return;
        if (reviewItems.length === 0) return;

        if (!reviewItems[idx]) {
            setIdx(Math.min(idx, reviewItems.length - 1));
        }
    }, [open, reviewItems, idx]);

    useEffect(() => {
        const cls = 'review-approval-open';
        if (approvalStatus) window.document.body.classList.add(cls);
        else window.document.body.classList.remove(cls);
    }, [approvalStatus]);

    useEffect(() => {
        if (open) setIdx(0);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setIdx(prev => Math.min(prev, Math.max(0, reviewItems.length - 1)));
    }, [reviewItems.length, open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const onKey = (e: KeyboardEvent) => {
            if (approvalStatus) return;

            if (e.key === 'ArrowLeft') {
                setIdx(i => (i > 0 ? i - 1 : i));
            }

            if (e.key === 'ArrowRight') {
                setIdx(i => (i < reviewItems.length - 1 ? i + 1 : i));
            }

            if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', onKey);

        return () => {
            window.removeEventListener('keydown', onKey);
        };
    }, [open, reviewItems.length, onClose, approvalStatus]);

    useEffect(() => {
        if (!open) return;
        if (!currentId) return;
        setDupModalData(null);
        setBusy(false);
        setApprovalStatus('');
    }, [open, currentId]);

    const goNext = useCallback(() => {
        setIdx(prev => (prev < reviewItems.length - 1 ? prev + 1 : prev));
    }, [reviewItems.length]);

    const goPrev = useCallback(() => {
        setIdx(prev => (prev > 0 ? prev - 1 : prev));
    }, []);

    const canPrev = useMemo(() => idx > 0, [idx]);
    const canNext = useMemo(
        () => idx < reviewItems.length - 1,
        [idx, reviewItems.length]
    );

    const systemValid = useMemo(() => {
        if (!current) return false;

        const validClassification =
            !current.photoPrediction?.classification ||
            current.photoPrediction?.classification?.validClassification;

        const validIdentification =
            !current.photoPrediction?.identification ||
            current.photoPrediction?.identification?.validIdentification;

        const validIntegrity =
            !current.photoIntegrity || current.photoIntegrity?.validIntegrity;

        return Boolean(
            validClassification && validIdentification && validIntegrity
        );
    }, [current]);

    const onApprove = useCallback(async () => {
        if (!current) return;

        if (systemValid) {
            const approval = {
                approvalStatusId: APPROVAL_STATUS.APPROVE.id,
                reasons: ['PHOTO_MEETS_REQUIREMENT'],
                additionalReasons: [],
                additionalNotes: '',
                reasonOther: '',
            };

            setBusy(true);
            try {
                await handleSaveApprovalPhotoRequirement(current.id, approval);
                await fetchAndSetHistory(current.id);
            } finally {
                setBusy(false);
            }
        } else {
            setApprovalStatus(APPROVAL_STATUS.APPROVE.id);
        }
    }, [
        current,
        systemValid,
        handleSaveApprovalPhotoRequirement,
        fetchAndSetHistory,
    ]);

    const onReject = useCallback(() => {
        setApprovalStatus(APPROVAL_STATUS.REJECT.id);
    }, []);

    const onAccept = useCallback(() => {
        setApprovalStatus(APPROVAL_STATUS.ACCEPT.id);
    }, []);

    const onRemove = useCallback(async () => {
        if (!current) return;

        setBusy(true);
        try {
            await handleRemovePhotoOnClick(current);
            if (idx >= reviewItems.length - 1) {
                setIdx(i => Math.max(0, i - 1));
            } else {
                goNext();
            }
        } finally {
            setBusy(false);
        }
    }, [current, handleRemovePhotoOnClick, idx, reviewItems.length, goNext]);

    const handleMetadataToggle = useCallback(() => {
        if (!current?.photoMetadata) return;
        setIsMetadataOpen(v => !v);
    }, [current?.photoMetadata]);

    const closeMetadata = useCallback(() => setIsMetadataOpen(false), []);

    useEffect(() => {
        if (!open) return;
        setIsMetadataOpen(false);
    }, [currentId, open]);

    useEffect(() => {
        if (!isMetadataOpen) return;

        const onDocMouseDown = (e: MouseEvent) => {
            const el = metaAnchorRef.current;
            if (!el) return;
            if (e.target instanceof Node && !el.contains(e.target)) {
                closeMetadata();
            }
        };

        window.document.addEventListener('mousedown', onDocMouseDown);
        return () =>
            window.document.removeEventListener('mousedown', onDocMouseDown);
    }, [isMetadataOpen, closeMetadata]);

    const kebab = useMemo(() => {
        if (!current?.requirement?.photo) return null;

        return (
            <ReviewModeActionMenuWrap>
                <TableActionsDropdown
                    busy={false}
                    disabled={!authenticationData.authenticatedUserCanEdit}
                    title='Review Mode Drawer Actions'
                    icon={<KebabMenuIcon />}
                    actions={[
                        {
                            name: 'Update Photo Type',
                            onClick: () => {
                                setOnEditDoneCallback(() => () => goNext());
                                setShowEditPhotoDrawer(true);
                            },
                            className: 'no-uppercase',
                        },
                        {
                            name: 'Remove',
                            onClick: onRemove,
                            className: `${styles.remove} no-uppercase remove-red`,
                        },
                    ]}
                />
            </ReviewModeActionMenuWrap>
        );
    }, [
        current,
        authenticationData.authenticatedUserCanEdit,
        goNext,
        onRemove,
    ]);

    const photoIndexLabel = useMemo(() => {
        if (!current) return '';

        const sameTypeUploaded = (photosAll || []).filter(
            p =>
                p?.requirement?.photoTypeId ===
                    current?.requirement?.photoTypeId &&
                Boolean(p?.requirement?.photo)
        );
        const i = Math.max(
            0,
            sameTypeUploaded.findIndex(p => p.id === current.id)
        );
        let title = 'Photo';
        if (isVideo(current)) {
            title = 'Video';
        } else if (isPDF(current)) {
            title = 'Document';
        }
        return `${title} #${i + 1}`;
    }, [current, photosAll]);

    const name = useMemo(
        () => current?.requirement?.fileName || '—',
        [current]
    );

    const description = useMemo(() => {
        if (!current) return '';
        return (
            (current as any)?.requirement?.description ||
            (current as any)?.requirement?.display ||
            ''
        );
    }, [current]);

    const uploadedBy = useMemo(
        () => current?.requirement?.uploadedBy || '',
        [current]
    );

    const uploadedOn = useMemo(() => {
        if (!current?.created) return '';
        return displayDateTime(current.created);
    }, [current]);

    const photoIdentification = useMemo(() => {
        if (!current) return 'N/A';

        if (!current.photoPrediction) return 'N/A';

        return current.photoPrediction.identification?.validIdentification
            ? 'PASS'
            : 'FAIL';
    }, [current]);

    const integrityDetails = useMemo(
        () =>
            getIntegrityDetails(
                current?.photoIntegrity,
                current?.photoMetadata,
                (document as any).property?.timezone
            ),
        [current?.photoIntegrity, current?.photoMetadata, document]
    );

    const classificationPct = useMemo(() => {
        if (!current?.photoPrediction?.classification) return null;
        return (
            (current.photoPrediction.classification as any)
                .classificationScore ?? null
        );
    }, [current]);

    const photoIdentificationLabel = useMemo(() => {
        if (photoIdentification === 'PASS') return 'Pass';
        if (photoIdentification === 'FAIL') return 'Fail';
        return 'N/A';
    }, [photoIdentification]);

    const photoIntegrityLabel = useMemo(() => {
        if (!current?.photoIntegrity) return 'N/A';

        return current.photoIntegrity.validIntegrity ? 'Pass' : 'Fail';
    }, [current]);

    const url = useMemo(() => {
        return current?.requirement?.url || (current as any)?.url;
    }, [current]);

    const isCurrentVideo = useMemo(
        () => (current ? isVideo(current) : false),
        [current]
    );

    const isCurrentDocument = useMemo(() => {
        const mime = current?.requirement?.mimeType || current?.mimeType || '';
        return mime.startsWith('application/pdf');
    }, [current]);

    const clearApprovalHistory = useCallback(() => {}, []);

    const fetchDuplicateRefDocs = useCallback(async () => {
        const dupList =
            current?.photoIntegrity?.validateDuplication?.duplicateList;

        const docIds = dupList
            ?.filter((m: any) => m.refDocumentId)
            ?.map((m: any) => m.refDocumentId);

        if (!Array.isArray(docIds) || docIds.length === 0) return [];

        const docs = await getUsagePhotoByDocIds(docIds);

        return docs.filter((doc: any) =>
            dupList?.find(
                (m: any) =>
                    m.refDocumentId === doc.id &&
                    m.refDocumentType === doc.documentTypeId
            )
        );
    }, [current]);

    const handleViewInstances = useCallback(async () => {
        try {
            setBusy(true);
            const refDocs = await fetchDuplicateRefDocs();
            setDupModalData({ refDocs });
        } finally {
            setBusy(false);
        }
    }, [fetchDuplicateRefDocs]);

    const currentPhotoType = useMemo(() => {
        if (!current?.requirement?.photoTypeId) return undefined;
        return (photoTypes || []).find(
            pt => pt.id === current.requirement.photoTypeId
        );
    }, [current?.requirement?.photoTypeId, photoTypes]);

    const headerTitle = useMemo(() => {
        return (
            currentPhotoType?.display || current?.requirement?.display || '—'
        );
    }, [currentPhotoType?.display, current?.requirement?.display]);

    if (!open) return null;

    if (isDone) {
        return (
            <ReviewModal
                show={open}
                onHide={onClose}
                centered
                backdrop='static'
                keyboard
                className='review-mode-modal'
                backdropClassName='review-mode-backdrop'
            >
                <ModalHeaderBar>
                    <div />
                    <button
                        type='button'
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            fontSize: 20,
                            lineHeight: 1,
                            cursor: 'pointer',
                        }}
                        aria-label='Close'
                    >
                        ×
                    </button>
                </ModalHeaderBar>

                <Modal.Body>
                    <div
                        style={{
                            height: '70vh',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 16,
                        }}
                    >
                        <div style={{ fontSize: 16, fontWeight: 500 }}>
                            All photos have been reviewed.
                        </div>

                        <button
                            type='button'
                            onClick={onClose}
                            style={{
                                padding: '6px 18px',
                                borderRadius: 2,
                                border: '1px solid #ccc',
                                background: '#fff',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            Close
                        </button>
                    </div>
                </Modal.Body>
            </ReviewModal>
        );
    }

    if (!current) {
        return (
            <ReviewModal
                show={open}
                onHide={onClose}
                centered
                backdrop='static'
                keyboard
                className='review-mode-modal'
                backdropClassName='review-mode-backdrop'
            >
                <ModalHeaderBar>
                    <div />
                    <button type='button' onClick={onClose} aria-label='Close'>
                        ×
                    </button>
                </ModalHeaderBar>

                <Modal.Body>
                    <div
                        style={{
                            height: '70vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        Loading next photo...
                    </div>
                </Modal.Body>
            </ReviewModal>
        );
    }

    return (
        <>
            <ReviewModeLayerFix />

            <ReviewModal
                show={open}
                onHide={onClose}
                centered
                backdrop='static'
                keyboard
                className='review-mode-modal'
                backdropClassName='review-mode-backdrop'
            >
                <ModalHeaderBar>
                    <ModalTitle>
                        <InfoIcon />
                        <span>{headerTitle}</span>
                        {photoRequirementMet(current) && <CheckIcon />}
                    </ModalTitle>

                    <button
                        type='button'
                        onClick={onClose}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            fontSize: 20,
                            lineHeight: 1,
                            cursor: 'pointer',
                        }}
                        aria-label='Close'
                    >
                        ×
                    </button>
                </ModalHeaderBar>

                <Modal.Body>
                    {approvalStatus && (
                        <PhotoRequirementsApprovalModal
                            className='photo-requirements-approval-modal'
                            backdropClassName='photo-requirements-approval-backdrop'
                            photo={current}
                            initialType={approvalStatus}
                            handleClose={() => setApprovalStatus('')}
                            handleSave={async (photoId, approval) => {
                                setBusy(true);
                                try {
                                    await handleSaveApprovalPhotoRequirement(
                                        photoId,
                                        approval
                                    );
                                    await fetchAndSetHistory(photoId);
                                    setApprovalStatus('');
                                } finally {
                                    setBusy(false);
                                }
                            }}
                            clearHistory={clearApprovalHistory}
                        />
                    )}

                    {showEditPhotoDrawer && (
                        <EditPhotoRequirementsModal
                            photos={photosAll}
                            selectedPhoto={current}
                            currentPhotoTypeId={
                                current?.requirement?.photoTypeId
                            }
                            handleClose={() => {
                                setShowEditPhotoDrawer(false);
                                onEditDoneCallback?.();
                                setOnEditDoneCallback(null);
                            }}
                            handleEditUploadedPhotoRequirement={async (
                                ...args: [number, string, string, boolean]
                            ) => {
                                await handleEditUploadedPhotoRequirement(
                                    ...args
                                );
                            }}
                            photoTypeRequirements={photoTypeRequirements}
                            mimeType={current?.requirement?.mimeType}
                            currentRequiresPullAndClean={
                                current?.requirement?.requiresPullAndClean
                            }
                        />
                    )}

                    {dupModalData && (
                        <DocumentsPhotoModal
                            handleCloseOnClick={() => setDupModalData(null)}
                            documents={dupModalData.refDocs}
                            metadata={current?.photoMetadata}
                        />
                    )}

                    <BodyGrid>
                        <MediaStage>
                            <ArrowBtn
                                side='left'
                                onClick={() => canPrev && goPrev()}
                                disabled={!canPrev}
                            >
                                ‹
                            </ArrowBtn>

                            <ArrowBtn
                                side='right'
                                onClick={() => canNext && goNext()}
                                disabled={!canNext}
                            >
                                ›
                            </ArrowBtn>

                            <SideBlack />
                            <MediaCenter>
                                {kebab ? (
                                    <KebabMenuButton>{kebab}</KebabMenuButton>
                                ) : null}

                                {isCurrentVideo ? (
                                    <video controls src={url} />
                                ) : isCurrentDocument ? (
                                    <iframe
                                        src={url}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                        }}
                                        title='document preview'
                                    />
                                ) : (
                                    <img src={url} alt='review' />
                                )}
                            </MediaCenter>
                            <SideBlack />
                        </MediaStage>

                        <FooterBar>
                            <FooterMetaRow>
                                <FooterGrid3>
                                    <Cell>
                                        <PillRow>
                                            <StyledChip>
                                                {photoIndexLabel}
                                            </StyledChip>
                                        </PillRow>
                                        {current?.requirement
                                            ?.requiresPullAndClean && (
                                            <RequiresPullAndCleanLabel>
                                                Requires Pull and Clean
                                            </RequiresPullAndCleanLabel>
                                        )}
                                    </Cell>

                                    <Cell />

                                    <CellRightTop>
                                        <MetaAnchor ref={metaAnchorRef}>
                                            {current?.photoMetadata && (
                                                <MetaLinkBtn
                                                    type='button'
                                                    onClick={
                                                        handleMetadataToggle
                                                    }
                                                >
                                                    {isCurrentDocument
                                                        ? 'DOCUMENT METADATA'
                                                        : isCurrentVideo
                                                          ? 'VIDEO METADATA'
                                                          : 'PHOTO METADATA'}
                                                </MetaLinkBtn>
                                            )}

                                            {isMetadataOpen &&
                                                current?.photoMetadata && (
                                                    <MetadataPopover role='dialog'>
                                                        <MetadataCard>
                                                            <PhotoMetadataModal
                                                                variant='drawer'
                                                                title={
                                                                    isCurrentDocument
                                                                        ? 'Document Metadata'
                                                                        : 'Photo Metadata'
                                                                }
                                                                metadata={
                                                                    current.photoMetadata
                                                                }
                                                                photoIntegrity={
                                                                    current.photoIntegrity
                                                                }
                                                                document={
                                                                    document
                                                                }
                                                                handleCloseOnClick={
                                                                    closeMetadata
                                                                }
                                                                handleReusedViewOnClick={() => {}}
                                                            />
                                                        </MetadataCard>
                                                    </MetadataPopover>
                                                )}
                                        </MetaAnchor>
                                    </CellRightTop>
                                </FooterGrid3>

                                <FooterGrid3 style={{ marginTop: 10 }}>
                                    <Cell>
                                        <MetaLabel>Name</MetaLabel>
                                        <MetaValue>{name}</MetaValue>

                                        <DescUnderName>
                                            <MetaLabel>Description</MetaLabel>
                                            <MetaValue>
                                                {description || ''}
                                            </MetaValue>
                                        </DescUnderName>
                                    </Cell>
                                    <Cell>
                                        <MetaLabel>Uploaded by</MetaLabel>
                                        <MetaValue>{uploadedBy}</MetaValue>

                                        <StackGap>
                                            <MetaLabel>Uploaded on</MetaLabel>
                                            <MetaValue>{uploadedOn}</MetaValue>
                                        </StackGap>
                                    </Cell>
                                    <Cell>
                                        {classificationPct != null && (
                                            <>
                                                <MetaLabel>
                                                    Classification{' '}
                                                    <span
                                                        style={{
                                                            color:
                                                                classificationPct <
                                                                50
                                                                    ? Config
                                                                          .color
                                                                          .red
                                                                    : Config
                                                                          .color
                                                                          .motiliLightBlue,
                                                        }}
                                                    >
                                                        {classificationPct}%
                                                        Clean
                                                    </span>
                                                </MetaLabel>
                                                <ReviewCleanDirtyBar>
                                                    <span>Dirty</span>
                                                    <ReviewBarTrack>
                                                        <ReviewBarFill
                                                            $pct={
                                                                classificationPct
                                                            }
                                                            $invalid={
                                                                classificationPct <
                                                                50
                                                            }
                                                        />
                                                    </ReviewBarTrack>
                                                    <span>Clean</span>
                                                </ReviewCleanDirtyBar>
                                                <StackGap />
                                            </>
                                        )}

                                        <MetaLabel>Identification</MetaLabel>
                                        <ResultText
                                            invalid={
                                                photoIdentification === 'FAIL'
                                            }
                                            pass={
                                                photoIdentification === 'PASS'
                                            }
                                        >
                                            {photoIdentificationLabel}
                                        </ResultText>

                                        <StackGap>
                                            <MetaLabel>
                                                Photo integrity
                                            </MetaLabel>
                                            <ResultText
                                                invalid={
                                                    integrityDetails.length > 0
                                                }
                                                pass={
                                                    current?.photoIntegrity
                                                        ?.validIntegrity ===
                                                    true
                                                }
                                            >
                                                {photoIntegrityLabel}
                                            </ResultText>
                                        </StackGap>

                                        {integrityDetails.length > 0 && (
                                            <StackGap>
                                                <MetaLabel>
                                                    Failure reason
                                                </MetaLabel>
                                                {integrityDetails.map(
                                                    (detail, i) => (
                                                        <div key={i}>
                                                            <FailureRow>
                                                                <span>
                                                                    {
                                                                        detail.label
                                                                    }
                                                                </span>
                                                                {detail.showInstancesBtn && (
                                                                    <InfoDotSmall
                                                                        onClick={
                                                                            handleViewInstances
                                                                        }
                                                                        style={{
                                                                            opacity:
                                                                                busy
                                                                                    ? 0.5
                                                                                    : 1,
                                                                            pointerEvents:
                                                                                busy
                                                                                    ? 'none'
                                                                                    : 'auto',
                                                                        }}
                                                                    />
                                                                )}
                                                            </FailureRow>
                                                            {detail.value && (
                                                                <div
                                                                    style={{
                                                                        color: '#333',
                                                                        fontWeight: 400,
                                                                        fontSize: 13,
                                                                        marginTop: 2,
                                                                    }}
                                                                >
                                                                    {
                                                                        detail.value
                                                                    }
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                )}
                                            </StackGap>
                                        )}
                                    </Cell>
                                </FooterGrid3>
                            </FooterMetaRow>

                            <FooterActionsRow>
                                <FooterActions>
                                    <ActionBtn
                                        variant='primary'
                                        disabled={!showApprovalButtons || busy}
                                        onClick={onApprove}
                                    >
                                        Approve
                                    </ActionBtn>
                                    <ActionBtn
                                        variant='danger'
                                        disabled={!showApprovalButtons || busy}
                                        onClick={onReject}
                                    >
                                        Reject
                                    </ActionBtn>
                                    <ActionBtn
                                        disabled={!showApprovalButtons || busy}
                                        onClick={onAccept}
                                    >
                                        Accept
                                    </ActionBtn>
                                </FooterActions>
                            </FooterActionsRow>
                        </FooterBar>
                    </BodyGrid>
                </Modal.Body>
            </ReviewModal>
        </>
    );
}

const ReviewModal = styled(Modal)`
    &.review-mode-modal {
        z-index: 1055 !important;
    }

    /* full screen */
    .modal-dialog {
        width: 100vw !important;
        max-width: 100vw !important;
        height: 100vh !important;
        margin: 0 !important;
        padding: 0 !important;
    }

    .modal-content {
        border-radius: 0 !important;
        overflow: hidden;
        height: 100vh !important;
        pointer-events: all;
    }

    .modal-body {
        padding: 0;
        height: calc(100vh - 54px);
        overflow: hidden;
    }
`;
const ModalHeaderBar = styled.div`
    height: 54px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 14px;
    border-bottom: 1px solid #e6e6e6;
    background: #fff;
`;

const ModalTitle = styled.div`
    font-size: 24px;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const BodyGrid = styled.div`
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-columns: 1fr;
    height: calc(100vh - 54px);
    overflow: hidden;
    background: #fff;
`;

const MediaStage = styled.div`
    position: relative;
    background: #000;
    overflow: hidden;
    min-height: 0;

    display: grid;
    grid-template-columns: 1fr minmax(0, var(--frame-w)) 1fr;
    align-items: center;

    --frame-w: 1344px;
`;

const SideBlack = styled.div`
    height: 100%;
    background: #000;
    min-height: 100%;
    align-self: stretch;
`;

const MediaCenter = styled.div`
    width: 100%;
    height: 100%;
    max-width: 1344px;
    margin: 0 auto;

    background: #000;
    overflow: hidden;

    display: flex;
    align-items: center;
    justify-content: center;

    img,
    video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
    }
`;

const ArrowBtn = styled.button<{ side: 'left' | 'right' }>`
    position: absolute;
    top: 50%;
    ${({ side }) => (side === 'left' ? 'left: 18px;' : 'right: 18px;')}
    transform: translateY(-50%);
    width: 42px;
    height: 42px;
    border-radius: 999px;
    border: 1px solid #d9d9d9;
    background: #fff;
    cursor: pointer;
    z-index: 5;

    font-size: 20px;
    font-weight: 900;
    line-height: 1;

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
`;

const MetaLabel = styled.div<{ $invalid?: boolean }>`
    color: ${({ $invalid }) => ($invalid ? Config.color.red : '#000')};
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 2px;
`;

const MetaValue = styled.div`
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 1.35;
    color: #333;
    min-width: 0;
`;

const FooterActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const ActionBtn = styled.button<{ variant?: 'primary' | 'danger' }>`
    min-width: 86px;
    height: 34px;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;

    border: 1px solid #d9d9d9;
    background: #fff;
    color: #333;

    ${({ variant }) =>
        variant === 'primary'
            ? `
        background: ${Config.color.motiliLightBlue};
        color: #fff;
        border-color: ${Config.color.motiliLightBlue};
      `
            : ''}

    ${({ variant }) =>
        variant === 'danger'
            ? `
        background: #fff;
        color: ${Config.color.red};
        border-color: ${Config.color.red};
      `
            : ''}

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ReviewModeActionMenuWrap = styled.div`
    .no-uppercase,
    .no-uppercase a,
    .no-uppercase span {
        text-transform: none !important;
        font-weight: 700 !important;
    }

    .remove-red,
    .remove-red a,
    .remove-red span {
        color: #e63232 !important;
        font-weight: 700 !important;
    }
`;

const FooterBar = styled.div`
    border-top: 1px solid #e6e6e6;
    background: #fff;
    overflow-y: visible;

    display: grid;
    grid-template-rows: auto 52px;
`;

const FooterMetaRow = styled.div`
    padding: 10px 14px 6px 14px;
    overflow: visible;
`;

const FooterActionsRow = styled.div`
    padding: 0 14px 10px 14px;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
`;

const ReviewModeLayerFix = createGlobalStyle`
    .review-mode-modal {
        z-index: 1055 !important;
    }
    
    .review-mode-backdrop {
        z-index: 1054 !important;
        pointer-events: none !important;
    }

    .photo-requirements-approval-modal {
        z-index: 2055 !important;
    }

    .photo-requirements-approval-backdrop {
        z-index: 2054 !important;
        pointer-events: none !important;
    }

    body.modal-open {
        overflow: hidden !important;
        padding-right: 0 !important;
    }
`;

const InfoIcon = styled(InfoOutlinedIcon)`
    color: ${Config.color.motiliLightBlue};
`;

const CheckIcon = () => (
    <img src={checkedIcon} alt='checked' style={{ width: 14, height: 14 }} />
);
const FooterGrid3 = styled.div`
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(220px, 1fr) minmax(
            220px,
            1fr
        );
    gap: 10px;
    font-size: 12px;
    align-items: start;
`;

const Cell = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const CellRightTop = styled(Cell)`
    align-items: flex-end;
    text-align: right;
    justify-content: flex-start;
`;

const ReviewCleanDirtyBar = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #666;
    margin-top: 2px;
    max-width: 320px;
`;

const ReviewBarTrack = styled.div`
    flex: 1;
    height: 6px;
    border-radius: 999px;
    background: #e0e0e0;
    overflow: hidden;
`;

const ReviewBarFill = styled.div<{ $pct: number; $invalid?: boolean }>`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${({ $invalid }) =>
        $invalid ? Config.color.red : Config.color.motiliLightBlue};
    border-radius: 999px;
`;

const MetaLinkBtn = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    color: ${Config.color.motiliLightBlue};
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    text-decoration: underline;
`;

const MetaAnchor = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`;

const DescUnderName = styled.div`
    margin-top: 10px;
`;

const ResultText = styled.div<{ invalid?: boolean; pass?: boolean }>`
    font-weight: 400;
    font-size: 16px;
    color: ${({ invalid, pass }) =>
        invalid ? Config.color.red : pass ? '#2e7d32' : '#333'};
`;

const FailureRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${Config.color.red};
    font-weight: 400;
    font-size: 16px;
`;

const InfoDotSmall = styled(InfoOutlinedIcon)`
    cursor: pointer;
    font-size: 20px !important;
    color: ${Config.color.motiliLightBlue};
`;

const PillRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const RequiresPullAndCleanLabel = styled.div`
    margin-top: 16px;
    font-size: 12px;
    font-weight: 700;
    color: ${Config.color.red};
`;

const StackGap = styled.div`
    margin-top: 10px;
`;

const MetadataPopover = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    top: auto;
    right: 0;
    width: 260px;
    z-index: 9999;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    padding: 12px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
`;

const MetadataCard = styled.div`
    position: relative;
    width: 260px;
    max-width: 260px;

    padding: 12px 40px 12px 12px;

    max-height: 340px;
    overflow: auto;

    * {
        box-sizing: border-box;
    }

    .row,
    .metadata-row {
        min-width: 0;
    }

    .value,
    .metadata-value,
    td,
    p,
    div {
        overflow-wrap: anywhere;
        word-break: break-word;
    }
`;
